import { CalendarDays, MapPin, Wallet } from "lucide-react";
import type { InspirationDestination } from "@/lib/inspiration.functions";

export type InspirationCardsCopy = {
  bestSeason: string;
  idealDays: string;
  budgetLow: string;
  budgetMedium: string;
  budgetHigh: string;
  createRouteHere: string;
  photoAlt: string;
};

export function inspirationCardsCopy(lang: string): InspirationCardsCopy {
  const pick = <T,>(m: Record<string, T>): T => m[lang] ?? m.en;
  return {
    bestSeason: pick({ pt: "Melhor época", en: "Best season", es: "Mejor época", fr: "Meilleure période", it: "Periodo migliore", de: "Beste Reisezeit", ja: "ベストシーズン" }),
    idealDays: pick({ pt: "dias ideais", en: "ideal days", es: "días ideales", fr: "jours idéaux", it: "giorni ideali", de: "ideale Tage", ja: "おすすめ日数" }),
    budgetLow: pick({ pt: "Económico", en: "Budget", es: "Económico", fr: "Économique", it: "Economico", de: "Günstig", ja: "低予算" }),
    budgetMedium: pick({ pt: "Médio", en: "Mid-range", es: "Medio", fr: "Moyen", it: "Medio", de: "Mittel", ja: "中予算" }),
    budgetHigh: pick({ pt: "Confortável", en: "Comfort", es: "Confortable", fr: "Confort", it: "Comfort", de: "Komfort", ja: "高め" }),
    createRouteHere: pick({ pt: "Criar roteiro aqui", en: "Create itinerary here", es: "Crear itinerario aquí", fr: "Créer un itinéraire ici", it: "Crea itinerario qui", de: "Route hier erstellen", ja: "ここで旅程を作成" }),
    photoAlt: pick({ pt: "Foto de", en: "Photo of", es: "Foto de", fr: "Photo de", it: "Foto di", de: "Foto von", ja: "写真：" }),
  };
}

export function InspirationCards({
  destinations,
  copy,
  onCreateRoute,
}: {
  destinations: InspirationDestination[];
  copy: InspirationCardsCopy;
  onCreateRoute: (destination: InspirationDestination) => void;
}) {
  const budgetLabel = (b: InspirationDestination["budget"]) =>
    b === "low" ? copy.budgetLow : b === "high" ? copy.budgetHigh : copy.budgetMedium;

  return (
    <ol className="mt-3 grid gap-3 sm:grid-cols-2">
      {destinations.map((d) => (
        <li
          key={`${d.name}|${d.country}`}
          className="overflow-hidden rounded-2xl border border-border/60 bg-background/70"
        >
          {d.photoUrl ? (
            <img
              src={d.photoUrl}
              alt={`${copy.photoAlt} ${d.name}`}
              loading="lazy"
              className="h-32 w-full object-cover"
            />
          ) : (
            <div className="flex h-32 w-full items-center justify-center bg-muted/60">
              <MapPin className="h-6 w-6 text-muted-foreground" aria-hidden />
            </div>
          )}
          <div className="p-3">
            <p className="text-sm font-semibold text-foreground">
              {d.name}
              <span className="font-normal text-muted-foreground"> · {d.country}</span>
            </p>
            <p className="mt-1 text-[12px] leading-snug text-muted-foreground">{d.summary}</p>
            <div className="mt-2 flex flex-wrap gap-1.5 text-[11px]">
              <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-muted-foreground">
                <CalendarDays className="h-3 w-3" aria-hidden />
                {copy.bestSeason}: {d.bestSeason}
              </span>
              <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-muted-foreground">
                {d.idealDays} {copy.idealDays}
              </span>
              <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-muted-foreground">
                <Wallet className="h-3 w-3" aria-hidden />
                {budgetLabel(d.budget)}
              </span>
            </div>
            <button
              type="button"
              onClick={() => onCreateRoute(d)}
              className="mt-3 w-full rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground"
            >
              {copy.createRouteHere}
            </button>
          </div>
        </li>
      ))}
    </ol>
  );
}
